import React, { useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useLanguageStore } from '@/store/languageStore';
import { getTheme } from './theme/theme';
import { arEG, enUS } from '@mui/material/locale';
import { useAuth } from './hooks/useAuth';

// Layout
import AppLayout from './components/layout/AppLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ReviewPage from './pages/ReviewPage';
import IndexChannelPage from './pages/IndexChannelPage';
import TimelinePage from './pages/TimelinePage';
import LibrariesPage from './pages/LibrariesPage';
import LibraryReviewPage from './pages/LibraryReviewPage';
import EntriesPage from './pages/EntriesPage';
import ChannelsPage from './pages/ChannelsPage';
import ChatPreviewPage from './pages/chatPreview/ChatPreviewPage';
import NotFoundPage from './pages/NotFoundPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import SyncPage from './pages/SyncPage';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Slug-to-path mapping for redirects
const SLUG_PATH_MAP: Record<string, string> = {
  'dashboard': '/',
  'review': '/review',
  'index-channel': '/index-channel',
  'timeline': '/timeline',
  'channels': '/channels',
  'libraries': '/libraries',
  'entries': '/entries',
  'chat-preview': '/chat-preview',
};

// Get the first page the user is allowed to access
const useFirstAllowedPath = () => {
  const { user } = useAuth();
  if (!user) return '/';
  if (user.manuscript_role === 'admin' || !user.allowed_pages) return '/';
  if (user.allowed_pages.length === 0) return '/';
  return SLUG_PATH_MAP[user.allowed_pages[0]] || '/';
};

// Page access route - checks if user has access to a specific page
const PageAccessRoute: React.FC<{ slug: string; children: React.ReactElement }> = ({ slug, children }) => {
  const { hasPageAccess } = useAuth();
  const firstAllowedPath = useFirstAllowedPath();

  if (!hasPageAccess(slug)) {
    return <Navigate to={firstAllowedPath} replace />;
  }

  return children;
};

// Admin-only route
const AdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAdmin } = useAuth();
  const firstAllowedPath = useFirstAllowedPath();

  if (!isAdmin()) {
    return <Navigate to={firstAllowedPath} replace />;
  }

  return children;
};

// Main App Component
const App: React.FC = () => {
  const language = useLanguageStore((state) => state.language);

  const theme = useMemo(() => {
    const baseTheme = getTheme(language);
    const muiLocale = language === 'ar' ? arEG : enUS;
    return createTheme(baseTheme, muiLocale);
  }, [language]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
  }, [language]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected Routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<PageAccessRoute slug="dashboard"><DashboardPage /></PageAccessRoute>} />
              <Route path="/review" element={<PageAccessRoute slug="review"><ReviewPage /></PageAccessRoute>} />
              <Route path="/index-channel" element={<PageAccessRoute slug="index-channel"><IndexChannelPage /></PageAccessRoute>} />
              <Route path="/timeline" element={<PageAccessRoute slug="timeline"><TimelinePage /></PageAccessRoute>} />
              <Route path="/channels" element={<PageAccessRoute slug="channels"><ChannelsPage /></PageAccessRoute>} />
              <Route path="/libraries" element={<PageAccessRoute slug="libraries"><LibrariesPage /></PageAccessRoute>} />
              <Route path="/libraries/:libraryId/review" element={<PageAccessRoute slug="libraries"><LibraryReviewPage /></PageAccessRoute>} />
              <Route path="/entries" element={<PageAccessRoute slug="entries"><EntriesPage /></PageAccessRoute>} />
              <Route path="/chat-preview" element={<PageAccessRoute slug="chat-preview"><ChatPreviewPage /></PageAccessRoute>} />
              <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
              <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
              <Route path="/sync" element={<AdminRoute><SyncPage /></AdminRoute>} />
            </Route>

            {/* 404 Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
